import type { SlicedCustomGeoJSON } from "@/cache/CustomGeoJSONCache/types";
import type { ConditionOptions } from "@/utility/conditionalExpressions";
import type { Feature, GeoJsonProperties, Geometry, LineString, Point, Polygon } from "geojson";

import { hasTaCodePoints } from "@/cache/CustomGeoJSONCache/hasTaCodePoints";
import { hydrateLineStringFeatures } from "@/cache/CustomGeoJSONCache/hydrateLineStringFeatures";
import { hydrateLineStringFeaturesAdvancedThreshold } from "@/cache/CustomGeoJSONCache/hydrateLineStringFeaturesAdvancedThreshold";
import { hydratePointFeatures } from "@/cache/CustomGeoJSONCache/hydratePointFeatures";
import { hydratePolygonFeatures } from "@/cache/CustomGeoJSONCache/hydratePolygonFeatures";
import { hydratePolygonFeaturesAdvancedThreshold } from "@/cache/CustomGeoJSONCache/hydratePolygonFeaturesAdvancedThreshold";
import type { RetryState } from "@/cache/SpatioTemporalTileCache/RetryState";
import type { CustomGeoJsonRequestOption } from "@/layers/geojson/custom";
import type { DateTime } from "luxon";

/**
 * LEGACY FRONTEND HYDRATION
 *
 * Can be removed once geojson backend hydration is the only supported implementation:
 * - hydrateLineStringFeatures
 * - hydrateLineStringFeaturesAdvancedThreshold
 * - hydratePolygonFeatures
 * - hydratePolygonFeaturesAdvancedThreshold
 * - hydratePointFeatures
 * - processCustomGeoJson
 */

export type CustomGeojsonProperties = {
  threshold_color: string;
  threshold_value: number;
  threshold_condition: ConditionOptions;
  id?: string | number;
};

export type EonGeojsonProperties = CustomGeojsonProperties & {
  name: string;
  lat: number;
  lon: number;
  id: number;
};

// Custom function for EON. Can make it generic later.
export function isEonGeojsonProperties(props: GeoJsonProperties | undefined): props is EonGeojsonProperties {
  // TODO: transformation needed. eon geojson has threshold_value => but we need "value"
  return (
    !!props &&
    typeof (props as any).name === "string" &&
    typeof (props as any).lat === "number" &&
    typeof (props as any).lon === "number" &&
    typeof (props as any).threshold_color === "string" &&
    typeof (props as any).threshold_value === "number" &&
    typeof (props as any).threshold_condition !== "undefined" &&
    typeof (props as any).id === "number"
  );
}
// EON custom feature for now. Use 17/05/2024 9:45 UTC to see it working
export function createPointByPolygonProperty(features: Array<Feature<Polygon, GeoJsonProperties>>) {
  const pointFeatures: Array<Feature<Point, GeoJsonProperties>> = [];

  for (const feature of features) {
    const props = feature.properties;

    if (!isEonGeojsonProperties(props)) {
      continue;
    }
    const { name, lat, lon, threshold_color, threshold_condition, threshold_value, id, ...rest } = props;

    pointFeatures.push({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [lon, lat],
      },
      properties: {
        // preserve EonGeoJsonProperties exactly
        name,
        lat,
        lon,
        threshold_color,
        threshold_condition,
        threshold_value,
        id,
        ...rest,
      },
    });
  }
  return pointFeatures;
}

export async function processCustomGeoJson(
  data: SlicedCustomGeoJSON,
  retry: RetryState,
  customGeoJsonRequestOption: CustomGeoJsonRequestOption,
  datetime: DateTime,
  parameter_unit: string,
  model: string,
  opts?: { hydrateLines?: boolean; hydratePolygons?: boolean },
): Promise<SlicedCustomGeoJSON> {
  const hydrateLines = opts?.hydrateLines ?? true;
  const hydratePolygons = opts?.hydratePolygons ?? true;

  const polygonPromises = !hydratePolygons
    ? []
    : customGeoJsonRequestOption.advancedThresholds?.length
      ? hydratePolygonFeaturesAdvancedThreshold(data.polygons.features, datetime, customGeoJsonRequestOption, retry)
      : hydratePolygonFeatures(
          data.polygons.features,
          parameter_unit,
          model,
          datetime,
          customGeoJsonRequestOption,
          retry,
        );

  const linePromises = !hydrateLines
    ? []
    : customGeoJsonRequestOption.advancedThresholds?.length
      ? hydrateLineStringFeaturesAdvancedThreshold(
          data.lineStrings.features,
          datetime,
          customGeoJsonRequestOption,
          retry,
        )
      : hydrateLineStringFeatures(
          data.lineStrings.features,
          parameter_unit,
          model,
          datetime,
          customGeoJsonRequestOption,
          retry,
        );

  const [hydratedPolygons, hydratedLines] = await Promise.all([
    hydratePolygons
      ? Promise.allSettled(polygonPromises).then((results) => {
          return results.flatMap((result) => {
            if (result.status === "rejected") {
              console.warn("Polygon hydration failed; skipping feature", result.reason);
              return [] as Array<Feature<Polygon, GeoJsonProperties>>;
            }
            return result.value as Array<Feature<Polygon, GeoJsonProperties>>;
          });
        })
      : Promise.resolve(data.polygons.features as Array<Feature<Polygon, GeoJsonProperties>>),
    hydrateLines
      ? (Promise.allSettled(linePromises).then((results) => {
          return results.flatMap((result) => {
            if (result.status === "rejected") {
              console.warn("Line hydration failed; skipping feature", result.reason);
              return [] as Array<Feature<LineString, GeoJsonProperties>>;
            }
            return result.value as Array<Feature<LineString, GeoJsonProperties>>;
          });
        }) as Promise<Array<Feature<LineString, GeoJsonProperties>>>)
      : Promise.resolve(data.lineStrings.features as Array<Feature<LineString, GeoJsonProperties>>),
  ]);

  return {
    points: data.points,
    polygons: { type: "FeatureCollection", features: hydratedPolygons as Array<Feature<Polygon, GeoJsonProperties>> },
    lineStrings: {
      type: "FeatureCollection",
      features: hydratedLines as Array<Feature<LineString, GeoJsonProperties>>,
    },
  };
}

export function isSlicedCustomGeoJSON(data: any): data is SlicedCustomGeoJSON {
  return (
    data &&
    typeof data === "object" &&
    data.polygons &&
    isFeatureCollection(data.polygons, "Polygon") &&
    data.lineStrings &&
    isFeatureCollection(data.lineStrings, "LineString") &&
    data.points &&
    isFeatureCollection(data.points, "Point")
  );
}

function isFeatureCollection(collection: any, type: string): boolean {
  return (
    collection &&
    typeof collection === "object" &&
    collection.type === "FeatureCollection" &&
    Array.isArray(collection.features) &&
    collection.features.every((feature: any) => feature.geometry && feature.geometry.type === type)
  );
}
